/* eslint-disable no-undef */
import { useState, useCallback } from "react";
import {
  Form,
  Input,
  Image,
  FormField,
  Button,
  Icon,
  Divider,
} from "semantic-ui-react";
import { useDropzone } from "react-dropzone";
import Swal from "sweetalert2";
import { image } from "../../../../../assets";
import { buildStorageUrl } from "../../../../../utils";
import { useAuth } from "../../../../../hooks";
import { User } from "../../../../../api";
import "./UserSidebarForm.scss";

const userController = new User();

export function UserSidebarForm({ user, onUserUpdate }) {
  const [phone2, setPhone2] = useState(user?.phone2 || "");
  const [address, setAddress] = useState(user?.address || "");
  const [loading, setLoading] = useState(false);
  const [avatar, setAvatar] = useState(null);
  const [fileAvatar, setFileAvatar] = useState(null);

  const { accessToken } = useAuth();

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    setAvatar(URL.createObjectURL(file));
    setFileAvatar(file);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/jpeg': [],
      'image/png': []
    },
    onDrop,
  });

  const getAvatar = () => {
    if (fileAvatar) {
      return avatar;
    } else if (user?.avatar) {
      return buildStorageUrl(user.avatar);
    }
    return image.noAvatar;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Verificar si hay cambios
    const hasChanges = 
      phone2 !== (user?.phone2 || "") || 
      address !== (user?.address || "") ||
      fileAvatar !== null;
    
    if (!hasChanges) {
      Swal.fire({
        icon: "warning",
        title: "Sin cambios",
        text: "No se detectaron cambios para actualizar.",
        confirmButtonText: "Entendido",
        confirmButtonColor: "#f39c12",
      });
      return;
    }

    setLoading(true);

    try {
      const userData = {
        phone2: phone2.trim(),
        address: address.trim(),
      };

      // Agregar el archivo del avatar si se seleccionó uno nuevo
      if (fileAvatar) {
        userData.fileAvatar = fileAvatar;
      }

      const response = await userController.updateUser(user.id, userData);
      
      Swal.fire({
        icon: "success",
        title: "¡Perfecto!",
        text: "Datos actualizados exitosamente.",
        confirmButtonText: "Continuar",
        confirmButtonColor: "#2185d0",
      });
      
      // Actualizar el usuario con los datos del servidor si hay callback
      if (onUserUpdate && response.user) {
        onUserUpdate(response.user);
      }
      
      // Resetear el estado del avatar después de actualizar exitosamente
      if (fileAvatar) {
        setAvatar(null);
        setFileAvatar(null);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error al actualizar",
        text: error.msg || 
              error.message || 
              "Error al actualizar los datos. Inténtalo de nuevo.",
        confirmButtonText: "Intentar de nuevo",
        confirmButtonColor: "#db2828",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Formulario para actualizar datos */}
      <Form className="user-sidebar-form" onSubmit={handleSubmit}>
        <div className="user-sidebar-form__avatar">
          <div {...getRootProps()} className="user-sidebar-form__avatar-dropzone">
            <input {...getInputProps()} />
            <Image
              avatar
              className="user-avatar"
              centered
              size="small"
              src={getAvatar()}
            />
            <div className="user-sidebar-form__avatar-overlay">
              <Icon name="camera" size="big" />
              <p>Cambiar foto</p>
            </div>
          </div>
        </div>
        <FormField>
          <Input
            icon="user circle"
            iconPosition="left"
            placeholder="Nombres y apellidos"
            value={`${user?.firstname || ""} ${user?.lastname || ""} ${
              user?.secondLastname || ""
            }`.trim()}
            readOnly
          />
        </FormField>
        <FormField>
          <Input
            icon="id card"
            iconPosition="left"
            placeholder="DNI"
            value={user?.dni}
            readOnly
          />
        </FormField>
        <FormField>
          <Input
            icon="at"
            iconPosition="left"
            placeholder="Correo electrónico"
            value={user?.email}
            readOnly
          />
        </FormField>
        <FormField>
          <Input
            icon="phone"
            iconPosition="left"
            placeholder="Teléfono principal"
            value={user?.phone || ""}
            readOnly
          />
        </FormField>
        <Divider className="divider-menu" />
        <FormField>
          <Input
            icon="phone"
            iconPosition="left"
            placeholder="Teléfono alternativo"
            value={phone2}
            onChange={(e) => setPhone2(e.target.value)}
          />
        </FormField>
        <FormField>
          <Input
            icon="map marker alternate"
            iconPosition="left"
            placeholder="Dirección"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </FormField>
        <Button
          fluid
          type="submit"
          disabled={loading}
          loading={loading}
        >
          Actualizar datos
        </Button>
      </Form>
    </>
  );
}
